import React from 'react';
import styled from 'styled-components';

import Caption from '@joinforma/ui/components/Caption';

import Currency from 'components/common/Currency';
import { PRODUCT_PAGE_ELEMENTS } from 'constants/testSelectors';
import { getPriceDifference } from 'utils/product';
import { getAmountToPoints } from 'utils/transaction';

import { Product } from '../types';
import { MINIMUM_SAVINGS_TO_DISPLAY } from './consts';

const Container = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: row;
`;

const PriceContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: row;
`;

const ProductPrice = styled.div``;

const SavingsBox = styled(Caption)`
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 44px;
  display: block;
  padding: 4px 8px;
`;

const Price = ({
  company,
  currency,
  decimalPlaces = 0,
  exchangeRate = 1,
  product,
}: {
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
        amount_to_points_ratio?: number;
      };
    };
  };
  currency: string;
  decimalPlaces?: number;
  exchangeRate?: number;
  product: Product;
}) => {
  const {
    term,
    usd_msrp: usdMSRP,
    usd_special_price: usdSpecialPrice,
  } = product;
  const { display_as_amount: displayAsAmount } =
    company.features.stipend_config;
  const salePrice = usdSpecialPrice || usdMSRP;
  const { price_difference: priceDifference } = getPriceDifference({
    msrp: usdMSRP,
    sale_price: salePrice,
  });
  const isSubscription = term === 'monthly';

  const FormattedCurrency = ({ amount }: { amount: number }) =>
    displayAsAmount ? (
      <Currency
        amount={amount}
        currency={currency}
        decimal_places={decimalPlaces}
        exchange_rate={exchangeRate}
      />
    ) : (
      <>
        {getAmountToPoints({
          company,
          format: true,
          round: true,
          total: amount,
        })}
      </>
    );

  return (
    <Container>
      <PriceContainer
        data-testid={PRODUCT_PAGE_ELEMENTS.PRODUCT_PRICE_CONTAINER}
      >
        <ProductPrice data-testid={PRODUCT_PAGE_ELEMENTS.PRODUCT_PRICE}>
          <FormattedCurrency amount={salePrice as number} />
          {isSubscription && '/mo'}
        </ProductPrice>
      </PriceContainer>
      {priceDifference > MINIMUM_SAVINGS_TO_DISPLAY && (
        <SavingsBox>
          Save <FormattedCurrency amount={priceDifference} />
        </SavingsBox>
      )}
    </Container>
  );
};

export default Price;
